import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import { fabric } from 'fabric';
import ImageFiltersPanel, { FILTER_PRESETS, FilterPreset } from './ImageFilters';
import BackgroundRemover from './BackgroundRemover';
import { GeneratedImage } from '../../types/imageStudio';
import {
  MousePointer2, Crop, Type, Pencil, Square, Circle, Minus, Triangle,
  RotateCcw, RotateCw, FlipHorizontal, FlipVertical,
  Undo2, Redo2, ZoomIn, ZoomOut, Trash2, Copy, Layers,
  Sliders, Eraser, Save, Download, Bold, Italic, Underline,
  AlignLeft, AlignCenter, AlignRight, ArrowUpRight, Star, Heart,
  Palette, Lock, Unlock, MoveUp, MoveDown, ChevronDown,
  ImagePlus, Eye, EyeOff, Sparkles, Loader2, RotateCw as RotateObj,
  Wand2, Send
} from 'lucide-react';
import { imageStudioService } from '../../services/imageStudioService';
import toast from 'react-hot-toast';
import AiCostBadge from '../ai/AiCostBadge';

type Tool = 'select' | 'crop' | 'text' | 'draw' | 'shape';

const FONTS = [
  'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana',
  'Courier New', 'Impact', 'Comic Sans MS', 'Trebuchet MS', 'Palatino',
  'Garamond', 'Bookman', 'Tahoma', 'Lucida Console', 'Monaco'
];

const SHAPE_TYPES = [
  { id: 'rect', label: 'Rectangle', icon: Square },
  { id: 'circle', label: 'Circle', icon: Circle },
  { id: 'triangle', label: 'Triangle', icon: Triangle },
  { id: 'line', label: 'Line', icon: Minus },
  { id: 'arrow', label: 'Arrow', icon: ArrowUpRight },
  { id: 'star', label: 'Star', icon: Star },
];

interface ImageEditorProps {
  image: GeneratedImage;
  onSave: (dataUrl: string, width: number, height: number, mimeType: string) => void;
  onBgRemoved?: (newImage: GeneratedImage) => void;
}

const ImageEditor: React.FC<ImageEditorProps> = ({ image, onSave, onBgRemoved }) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricRef = useRef<fabric.Canvas | null>(null);
  const [activeTool, setActiveTool] = useState<Tool>('select');
  // Espelho da ferramenta ativa para os listeners do canvas (closure do mount)
  const activeToolRef = useRef<Tool>('select');
  useEffect(() => { activeToolRef.current = activeTool; }, [activeTool]);
  const [zoom, setZoom] = useState(1);
  // The fabric canvas is fixed at 800×600 for editing UX, but the source
  // image may be much larger (e.g. 4K). We track its natural size so on
  // export we can render at that resolution instead of the editor preview's.
  const sourceSizeRef = useRef<{ w: number; h: number } | null>(null);
  // The factor used to fit the source into the editor canvas (≤ 1).
  // export multiplier = 1/fitScale → output matches the source resolution.
  const fitScaleRef = useRef<number>(1);
  // Current logical canvas dims (swap on 90° rotations, shrink on crop)
  const baseDimsRef = useRef<{ w: number; h: number } | null>(null);
  // Crop selection rectangle (never serialized, never exported)
  const cropRectRef = useRef<any>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showBgRemover, setShowBgRemover] = useState(false);
  const [activeFilter, setActiveFilter] = useState('normal');
  const [filterValues, setFilterValues] = useState(FILTER_PRESETS[0].values);
  // Cada entrada guarda também as dimensões do canvas — rotação e corte as
  // alteram, e o undo/redo precisa restaurá-las junto com os objetos.
  const historyRef = useRef<{ json: string; w: number; h: number }[]>([]);
  const historyIndexRef = useRef(-1);
  const isLoadingRef = useRef(false);
  const isLoadingHistoryRef = useRef(false);
  const saveHistoryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MAX_HISTORY = 50;

  // Drawing state
  const [brushColor, setBrushColor] = useState('#ff0000');
  const [brushSize, setBrushSize] = useState(3);

  // Selected object properties
  const [selectedObj, setSelectedObj] = useState<any>(null);
  const [objFill, setObjFill] = useState('#000000');
  const [objStroke, setObjStroke] = useState('#000000');
  const [objStrokeWidth, setObjStrokeWidth] = useState(2);
  const [objOpacity, setObjOpacity] = useState(100);
  const [objFontFamily, setObjFontFamily] = useState('Arial');
  const [objFontSize, setObjFontSize] = useState(32);
  const [objBold, setObjBold] = useState(false);
  const [objItalic, setObjItalic] = useState(false);
  const [objUnderline, setObjUnderline] = useState(false);
  const [objTextAlign, setObjTextAlign] = useState('left');
  const [objLocked, setObjLocked] = useState(false);
  const [showLayers, setShowLayers] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [layersList, setLayersList] = useState<any[]>([]);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Área de trabalho (dimensões finais em px, editáveis na ferramenta de corte)
  const [areaW, setAreaW] = useState<number>(0);
  const [areaH, setAreaH] = useState<number>(0);

  // AI state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiMode, setAiMode] = useState<'edit' | 'generate'>('generate');
  const [aiStyle, setAiStyle] = useState('realistic');
  const [aiLoading, setAiLoading] = useState(false);

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current) return;
    let disposed = false;

    const canvas = new fabric.Canvas(canvasRef.current, {
      // Fundo transparente: preserva o alpha de imagens sem fundo no export
      // (o xadrez do container aparece por trás como indicador visual)
      backgroundColor: '',
      preserveObjectStacking: true,
      width: 800,
      height: 600,
      selection: true,
    });
    fabricRef.current = canvas;

    // Load image via proxy
    if (image.id) {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('token');
      const proxyUrl = `${API_URL}/image-studio/${image.id}/proxy?token=${token}`;

      const htmlImg = new window.Image();
      htmlImg.crossOrigin = 'anonymous';
      // Fit canvas dimensions to the source's aspect ratio (within 800×600
      // bounds). This way the source fills the canvas exactly (no gray bars)
      // and exporting at multiplier = 1/fitScale yields exactly the source's
      // native resolution.
      const fitCanvasToSource = (nw: number, ch: number) => {
        const maxW = 800, maxH = 600;
        const ratio = nw / ch;
        let cw = maxW, chh = maxH;
        if (ratio > maxW / maxH) {
          chh = Math.round(maxW / ratio);
        } else {
          cw = Math.round(maxH * ratio);
        }
        canvas.setDimensions({ width: cw, height: chh });
        baseDimsRef.current = { w: cw, h: chh };
        return { cw, ch: chh };
      };

      htmlImg.onload = () => {
        if (disposed || !fabricRef.current) return;
        const nw = htmlImg.naturalWidth || 800;
        const nh = htmlImg.naturalHeight || 600;
        const { cw, ch } = fitCanvasToSource(nw, nh);
        const scale = Math.min(cw / nw, ch / nh, 1);
        sourceSizeRef.current = { w: nw, h: nh };
        fitScaleRef.current = scale;
        const fabricImg = new fabric.Image(htmlImg, {
          left: 0, top: 0,
          scaleX: scale, scaleY: scale,
          selectable: false, evented: false, name: 'backgroundImage'
        });
        // isLoadingRef evita entrada duplicada no histórico (object:added também salva)
        isLoadingRef.current = true;
        canvas.add(fabricImg);
        canvas.sendToBack(fabricImg);
        isLoadingRef.current = false;
        canvas.renderAll();
        saveHistoryNow();
      };
      htmlImg.onerror = () => {
        if (disposed) return;
        const fallback = new window.Image();
        fallback.crossOrigin = 'anonymous';
        fallback.onload = () => {
          if (disposed || !fabricRef.current) return;
          const nw = fallback.naturalWidth || 800;
          const nh = fallback.naturalHeight || 600;
          const { cw, ch } = fitCanvasToSource(nw, nh);
          const s = Math.min(cw / nw, ch / nh, 1);
          sourceSizeRef.current = { w: nw, h: nh };
          fitScaleRef.current = s;
          const fi = new fabric.Image(fallback, {
            left: 0, top: 0,
            scaleX: s, scaleY: s,
            selectable: false, evented: false, name: 'backgroundImage'
          });
          isLoadingRef.current = true;
          canvas.add(fi); canvas.sendToBack(fi);
          isLoadingRef.current = false;
          canvas.renderAll(); saveHistoryNow();
        };
        fallback.src = image.edited_url || image.original_url || '';
      };
      htmlImg.src = proxyUrl;
    }

    // Ferramenta de texto: clique na imagem cria o texto naquele ponto
    canvas.on('mouse:down', (e: any) => {
      if (activeToolRef.current !== 'text') return;
      // Clicou num objeto existente (não no fundo): deixa a seleção normal agir
      if (e.target && e.target.name !== 'backgroundImage') return;
      const p = canvas.getPointer(e.e);
      addText(p.x, p.y);
    });

    // Selection listeners
    canvas.on('selection:created', (e: any) => syncSelection(e.selected?.[0]));
    canvas.on('selection:updated', (e: any) => syncSelection(e.selected?.[0]));
    canvas.on('selection:cleared', () => { setSelectedObj(null); });
    canvas.on('object:modified', () => saveHistoryNow());
    canvas.on('object:added', () => { if (!isLoadingRef.current) saveHistoryNow(); });

    // Keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!fabricRef.current) return;
      const canvas = fabricRef.current;
      const target = e.target as HTMLElement;
      // Don't intercept if typing in an input/textarea
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;
      // Don't intercept if editing text on canvas
      const activeObj = canvas.getActiveObject();
      if (activeObj && (activeObj as any).isEditing) return;

      if (e.key === 'Escape' && cropRectRef.current) {
        e.preventDefault();
        cancelCrop();
        setActiveTool('select');
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && activeObj) {
        e.preventDefault();
        // Suporta seleção múltipla (activeSelection): remove cada objeto real
        const removable = canvas.getActiveObjects().filter((o: any) => o.name !== 'backgroundImage' && o.name !== 'cropRect');
        if (removable.length) {
          removable.forEach((o: any) => canvas.remove(o));
          canvas.discardActiveObject();
          canvas.renderAll();
          setSelectedObj(null);
          saveHistoryNow();
        }
      }
      if (e.key === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault();
        if (historyIndexRef.current > 0) { historyIndexRef.current--; loadFromHistory(); }
      }
      if ((e.key === 'y' && (e.ctrlKey || e.metaKey)) || (e.key === 'z' && (e.ctrlKey || e.metaKey) && e.shiftKey)) {
        e.preventDefault();
        if (historyIndexRef.current < historyRef.current.length - 1) { historyIndexRef.current++; loadFromHistory(); }
      }
      if (e.key === 'd' && (e.ctrlKey || e.metaKey) && activeObj) {
        e.preventDefault();
        duplicateSelected();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      disposed = true;
      if (saveHistoryTimerRef.current) clearTimeout(saveHistoryTimerRef.current);
      canvas.dispose();
      fabricRef.current = null;
    };
  }, [image]);

  const syncSelection = (obj: any) => {
    if (!obj) return;
    setSelectedObj(obj);
    setObjFill(obj.fill || '#000000');
    setObjStroke(obj.stroke || '#000000');
    setObjStrokeWidth(obj.strokeWidth || 0);
    setObjOpacity(Math.round((obj.opacity || 1) * 100));
    setObjLocked(!obj.selectable || false);
    if (obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text') {
      setObjFontFamily(obj.fontFamily || 'Arial');
      setObjFontSize(obj.fontSize || 32);
      setObjBold(obj.fontWeight === 'bold');
      setObjItalic(obj.fontStyle === 'italic');
      setObjUnderline(obj.underline || false);
      setObjTextAlign(obj.textAlign || 'left');
    }
  };

  const saveHistoryImmediate = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas || isLoadingHistoryRef.current) return;
    try {
      const json = JSON.stringify(canvas.toJSON(['name', 'selectable', 'evented']));
      historyRef.current.splice(historyIndexRef.current + 1);
      historyRef.current.push({ json, w: canvas.getWidth(), h: canvas.getHeight() });
      // Cap history size
      if (historyRef.current.length > MAX_HISTORY) {
        historyRef.current.shift();
      }
      historyIndexRef.current = historyRef.current.length - 1;
      setCanUndo(historyIndexRef.current > 0);
      setCanRedo(false);
    } catch (e) {
      console.warn('Failed to save history:', e);
    }
  }, []);

  // Debounced version for sliders/continuous changes
  const saveHistory = useCallback(() => {
    if (saveHistoryTimerRef.current) clearTimeout(saveHistoryTimerRef.current);
    saveHistoryTimerRef.current = setTimeout(() => saveHistoryImmediate(), 300);
  }, [saveHistoryImmediate]);

  // Immediate version for discrete actions (add object, delete, etc)
  const saveHistoryNow = saveHistoryImmediate;

  const loadFromHistory = () => {
    const canvas = fabricRef.current;
    if (!canvas || isLoadingHistoryRef.current) return;
    const entry = historyRef.current[historyIndexRef.current];
    if (!entry) return;
    if (cropRectRef.current) cancelCrop();
    isLoadingRef.current = true;
    isLoadingHistoryRef.current = true;
    try {
      canvas.setDimensions({ width: entry.w, height: entry.h });
      baseDimsRef.current = { w: entry.w, h: entry.h };
      canvas.loadFromJSON(JSON.parse(entry.json), () => {
        canvas.renderAll();
        isLoadingRef.current = false;
        isLoadingHistoryRef.current = false;
        setCanUndo(historyIndexRef.current > 0);
        setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
        setSelectedObj(null);
      });
    } catch {
      isLoadingRef.current = false;
      isLoadingHistoryRef.current = false;
    }
  };

  // ===== Tool handlers =====
  const handleToolChange = (tool: Tool) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    if (activeTool === 'crop' && tool !== 'crop') cancelCrop();
    setActiveTool(tool);
    canvas.isDrawingMode = tool === 'draw';
    if (tool === 'draw') {
      canvas.freeDrawingBrush.color = brushColor;
      canvas.freeDrawingBrush.width = brushSize;
    }
    if (tool === 'crop') {
      startCrop();
      // Sincroniza os campos de área com o tamanho final atual do export
      const m = Math.max(1, 1 / (fitScaleRef.current || 1));
      setAreaW(Math.round(canvas.getWidth() * m));
      setAreaH(Math.round(canvas.getHeight() * m));
    }
    // Ferramenta de texto: cursor de texto indica "clique para adicionar"
    canvas.defaultCursor = tool === 'text' ? 'text' : 'default';
    // Painel de Layers é persistente: só fecha pelo próprio botão Layers
    setShowFilters(false);
    setShowBgRemover(false);
    setShowAI(false);
  };

  // ===== Crop =====
  const startCrop = () => {
    const canvas = fabricRef.current;
    if (!canvas || cropRectRef.current) return;
    const cw = canvas.getWidth(), ch = canvas.getHeight();
    // isLoadingRef: o retângulo de corte não entra no histórico
    isLoadingRef.current = true;
    const rect = new fabric.Rect({
      left: cw * 0.1, top: ch * 0.1, width: cw * 0.8, height: ch * 0.8,
      fill: 'rgba(99,102,241,0.12)', stroke: '#4f46e5', strokeWidth: 1.5,
      strokeDashArray: [6, 4], strokeUniform: true,
      cornerColor: '#4f46e5', cornerStrokeColor: '#ffffff', transparentCorners: false,
      lockRotation: true, excludeFromExport: true, name: 'cropRect'
    });
    rect.setControlsVisibility({ mtr: false });
    canvas.add(rect);
    canvas.setActiveObject(rect);
    canvas.renderAll();
    isLoadingRef.current = false;
    cropRectRef.current = rect;
  };

  const cancelCrop = () => {
    const canvas = fabricRef.current;
    const rect = cropRectRef.current;
    cropRectRef.current = null;
    if (canvas && rect) {
      isLoadingRef.current = true;
      canvas.remove(rect);
      canvas.discardActiveObject();
      canvas.renderAll();
      isLoadingRef.current = false;
    }
  };

  const setCropRatio = (ratio: number | null) => {
    const canvas = fabricRef.current;
    const rect = cropRectRef.current;
    if (!canvas || !rect || !ratio) return;
    const cw = canvas.getWidth(), ch = canvas.getHeight();
    let w = cw * 0.8, h = w / ratio;
    if (h > ch * 0.9) { h = ch * 0.9; w = h * ratio; }
    rect.set({ width: w, height: h, scaleX: 1, scaleY: 1, left: (cw - w) / 2, top: (ch - h) / 2 });
    rect.setCoords();
    canvas.setActiveObject(rect);
    canvas.renderAll();
  };

  const applyCrop = () => {
    const canvas = fabricRef.current;
    const rect = cropRectRef.current;
    if (!canvas || !rect) return;
    const cw = canvas.getWidth(), ch = canvas.getHeight();
    // Interseção com o canvas: o usuário pode ter arrastado parte do retângulo pra fora
    const rl = Math.max(0, rect.left || 0);
    const rt = Math.max(0, rect.top || 0);
    const rr = Math.min(cw, (rect.left || 0) + rect.getScaledWidth());
    const rb = Math.min(ch, (rect.top || 0) + rect.getScaledHeight());
    const w = Math.round(rr - rl), h = Math.round(rb - rt);
    cancelCrop();
    setActiveTool('select');
    if (w < 10 || h < 10) {
      toast.error(t('imageStudio.editor.cropTooSmall', 'Área de corte muito pequena.'));
      return;
    }
    isLoadingRef.current = true;
    canvas.getObjects().forEach((o: any) => {
      o.set({ left: (o.left || 0) - rl, top: (o.top || 0) - rt });
      o.setCoords();
    });
    canvas.setDimensions({ width: w, height: h });
    baseDimsRef.current = { w, h };
    isLoadingRef.current = false;
    canvas.renderAll();
    saveHistoryNow();
  };

  // Redimensiona a área de trabalho (tamanho final do export) mantendo o
  // conteúdo centralizado: ampliar cria margens transparentes ao redor;
  // diminuir age como um corte central.
  const applyAreaResize = () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const m = Math.max(1, 1 / (fitScaleRef.current || 1));
    const newW = Math.round((areaW || 0) / m);
    const newH = Math.round((areaH || 0) / m);
    if (!areaW || !areaH || areaW < 10 || areaH < 10 || areaW > 5000 || areaH > 5000 || newW < 10 || newH < 10) {
      toast.error(t('imageStudio.editor.invalidArea', 'Dimensões inválidas (10 a 5000 px).'));
      return;
    }
    const hadCrop = !!cropRectRef.current;
    if (hadCrop) cancelCrop();
    const oldW = canvas.getWidth(), oldH = canvas.getHeight();
    const dx = (newW - oldW) / 2, dy = (newH - oldH) / 2;
    isLoadingRef.current = true;
    canvas.setDimensions({ width: newW, height: newH });
    baseDimsRef.current = { w: newW, h: newH };
    canvas.getObjects().forEach((o: any) => {
      o.set({ left: (o.left || 0) + dx, top: (o.top || 0) + dy });
      o.setCoords();
    });
    isLoadingRef.current = false;
    canvas.renderAll();
    saveHistoryNow();
    // Mantém os campos coerentes com o arredondamento aplicado
    setAreaW(Math.round(newW * m));
    setAreaH(Math.round(newH * m));
    if (hadCrop) startCrop();
  };

  const addText = (x?: number, y?: number) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const text = new fabric.IText('Text', {
      // Com posição do clique, o texto nasce centralizado no ponto clicado
      left: x !== undefined ? x : canvas.getWidth() / 2,
      top: y !== undefined ? y : canvas.getHeight() / 2,
      originX: 'center', originY: 'center',
      fontFamily: 'Arial', fontSize: 36, fill: '#ffffff',
      stroke: '#000000', strokeWidth: 0,
      fontWeight: 'normal', fontStyle: 'normal',
      shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.4)', blur: 5, offsetX: 2, offsetY: 2 })
    });
    canvas.add(text);
    canvas.setActiveObject(text);
    // Entra direto em edição com tudo selecionado: digitar substitui o "Text"
    (text as any).enterEditing?.();
    (text as any).selectAll?.();
    canvas.renderAll();
    setActiveTool('select');
    canvas.defaultCursor = 'default';
  };

  const addShape = (type: string) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    let shape: any;
    const cx = 200 + Math.random() * 300, cy = 150 + Math.random() * 200;
    const props = { fill: 'rgba(99,102,241,0.5)', stroke: '#4f46e5', strokeWidth: 2 };

    switch (type) {
      case 'rect':
        shape = new fabric.Rect({ left: cx, top: cy, width: 120, height: 80, rx: 8, ry: 8, ...props });
        break;
      case 'circle':
        shape = new fabric.Circle({ left: cx, top: cy, radius: 50, ...props });
        break;
      case 'triangle':
        shape = new fabric.Triangle({ left: cx, top: cy, width: 100, height: 100, ...props });
        break;
      case 'line':
        shape = new fabric.Line([cx, cy, cx + 150, cy], { stroke: '#4f46e5', strokeWidth: 3 });
        break;
      case 'arrow':
        shape = new fabric.Path('M 0 0 L 120 0 L 110 -10 M 120 0 L 110 10', {
          left: cx, top: cy, fill: '', stroke: '#4f46e5', strokeWidth: 3, strokeLineCap: 'round'
        });
        break;
      case 'star': {
        const pts = [];
        for (let i = 0; i < 10; i++) {
          const r = i % 2 === 0 ? 50 : 25;
          const a = (Math.PI / 5) * i - Math.PI / 2;
          pts.push({ x: r * Math.cos(a), y: r * Math.sin(a) });
        }
        shape = new fabric.Polygon(pts, { left: cx, top: cy, ...props });
        break;
      }
      default: return;
    }
    canvas.add(shape);
    canvas.setActiveObject(shape);
    canvas.renderAll();
    setActiveTool('select');
  };

  // ===== Property setters =====
  const updateProp = (prop: string, value: any) => {
    const canvas = fabricRef.current;
    const obj = canvas?.getActiveObject();
    if (!obj || !canvas) return;
    obj.set(prop as keyof fabric.Object, value);
    canvas.renderAll();
    saveHistory();
  };

  const setFill = (v: string) => { setObjFill(v); updateProp('fill', v); };
  const setStroke = (v: string) => { setObjStroke(v); updateProp('stroke', v); };
  const setStrokeW = (v: number) => { setObjStrokeWidth(v); updateProp('strokeWidth', v); };
  const setOpacity = (v: number) => { setObjOpacity(v); updateProp('opacity', v / 100); };

  const setFont = (v: string) => { setObjFontFamily(v); updateProp('fontFamily', v); };
  const setFSize = (v: number) => { setObjFontSize(v); updateProp('fontSize', v); };
  const toggleBold = () => { const v = !objBold; setObjBold(v); updateProp('fontWeight', v ? 'bold' : 'normal'); };
  const toggleItalic = () => { const v = !objItalic; setObjItalic(v); updateProp('fontStyle', v ? 'italic' : 'normal'); };
  const toggleUnderline = () => { const v = !objUnderline; setObjUnderline(v); updateProp('underline', v); };
  const setAlign = (v: string) => { setObjTextAlign(v); updateProp('textAlign', v); };

  const deleteSelected = () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    // Suporta seleção múltipla: canvas.remove(activeSelection) não remove os filhos
    const objs = canvas.getActiveObjects().filter((o: any) => o.name !== 'backgroundImage' && o.name !== 'cropRect');
    if (!objs.length) return;
    objs.forEach((o: any) => canvas.remove(o));
    canvas.discardActiveObject();
    canvas.renderAll();
    setSelectedObj(null);
    saveHistoryNow();
  };

  const duplicateSelected = () => {
    const canvas = fabricRef.current;
    const obj: any = canvas?.getActiveObject();
    if (!obj || !canvas || obj.name === 'cropRect' || obj.name === 'backgroundImage') return;
    obj.clone((cloned: any) => {
      canvas.discardActiveObject();
      cloned.set({ left: (cloned.left || 0) + 20, top: (cloned.top || 0) + 20 });
      if (cloned.type === 'activeSelection') {
        // Seleção múltipla: reinsere cada objeto individualmente (padrão fabric)
        cloned.canvas = canvas;
        cloned.forEachObject((o: any) => canvas.add(o));
        cloned.setCoords();
      } else {
        canvas.add(cloned);
        canvas.setActiveObject(cloned);
      }
      canvas.renderAll();
    });
  };

  const bringForward = () => { const c = fabricRef.current; const o = c?.getActiveObject(); if (o && c) { c.bringForward(o); c.renderAll(); } };
  const sendBackward = () => { const c = fabricRef.current; const o = c?.getActiveObject(); if (o && c) { c.sendBackwards(o); c.renderAll(); } };

  const toggleLock = () => {
    const canvas = fabricRef.current;
    const obj = canvas?.getActiveObject();
    if (!obj || !canvas) return;
    const locked = !objLocked;
    setObjLocked(locked);
    obj.set({ lockMovementX: locked, lockMovementY: locked, lockScalingX: locked, lockScalingY: locked, lockRotation: locked, hasControls: !locked });
    canvas.renderAll();
    refreshLayers();
  };

  // ===== Layers =====
  const refreshLayers = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const objs = canvas.getObjects()
      .filter((o: any) => o.name !== 'cropRect')
      .map((o: any, i: number) => ({
        index: i,
        name: o.name || (o.type === 'i-text' ? `Text: ${(o.text || '').substring(0, 15)}` : o.type === 'image' ? 'Image Layer' : `${o.type}`),
        type: o.type,
        visible: o.visible !== false,
        locked: !o.selectable || !!o.lockMovementX,
        obj: o
      }));
    setLayersList(objs.reverse()); // Top layer first
  }, []);

  // Refresh layers on selection/modification
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const refresh = () => refreshLayers();
    canvas.on('object:added', refresh);
    canvas.on('object:removed', refresh);
    canvas.on('object:modified', refresh);
    // Initial refresh
    const timer = setTimeout(refresh, 500);
    return () => { clearTimeout(timer); canvas.off('object:added', refresh); canvas.off('object:removed', refresh); canvas.off('object:modified', refresh); };
  }, [refreshLayers, image]);

  const addImageLayer = (e: React.ChangeEvent<HTMLInputElement>) => {
    const canvas = fabricRef.current;
    const file = e.target.files?.[0];
    if (!file || !canvas) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('imageStudio.reference.onlyImages', 'Only image files are allowed.'));
      e.target.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('imageStudio.reference.tooBig', 'Image must be under 10 MB.'));
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => toast.error(t('imageStudio.loadError', 'Failed to load image'));
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      const htmlImg = new window.Image();
      htmlImg.onerror = () => toast.error(t('imageStudio.loadError', 'Failed to load image'));
      htmlImg.onload = () => {
        const cw = canvas.getWidth();
        const ch = canvas.getHeight();
        const scale = Math.min(cw * 0.6 / htmlImg.naturalWidth, ch * 0.6 / htmlImg.naturalHeight, 1);
        const fabricImg = new fabric.Image(htmlImg, {
          left: cw / 2 - (htmlImg.naturalWidth * scale) / 2,
          top: ch / 2 - (htmlImg.naturalHeight * scale) / 2,
          scaleX: scale,
          scaleY: scale,
          name: `Layer: ${file.name.substring(0, 20)}`
        });
        canvas.add(fabricImg);
        canvas.setActiveObject(fabricImg);
        canvas.renderAll();
        saveHistoryNow();
        refreshLayers();
      };
      htmlImg.src = dataUrl;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const toggleLayerVisibility = (obj: any) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    obj.set('visible', !obj.visible);
    canvas.renderAll();
    saveHistoryNow();
    refreshLayers();
  };

  const selectLayer = (obj: any) => {
    const canvas = fabricRef.current;
    if (!canvas || !obj.selectable) return;
    canvas.setActiveObject(obj);
    canvas.renderAll();
  };

  // ===== AI Generation =====
  const aiLoadingRef = useRef(false);

  const getCanvasBase64 = (): string | null => {
    const canvas = fabricRef.current;
    if (!canvas) return null;
    try {
      return canvas.toDataURL({ format: 'jpeg', quality: 0.7, multiplier: 0.5 });
    } catch {
      return null;
    }
  };

  // Ajusta o canvas à proporção de uma nova imagem de fundo (dentro de 800×600)
  // e atualiza os refs usados no export para manter a resolução nativa.
  const fitCanvasToNewSource = (c: any, nw: number, nh: number) => {
    const maxW = 800, maxH = 600;
    const ratio = nw / nh;
    let cw = maxW, chh = maxH;
    if (ratio > maxW / maxH) chh = Math.round(maxW / ratio);
    else cw = Math.round(maxH * ratio);
    c.setDimensions({ width: cw, height: chh });
    baseDimsRef.current = { w: cw, h: chh };
    const scale = Math.min(cw / nw, chh / nh, 1);
    sourceSizeRef.current = { w: nw, h: nh };
    fitScaleRef.current = scale;
    return { cw, ch: chh, scale };
  };

  const loadAIImageToCanvas = (imageUrl: string, imageId: string, asBackground: boolean) => {
    const c = fabricRef.current;
    if (!c) return;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('token');
    const htmlImg = new window.Image();
    htmlImg.crossOrigin = 'anonymous';
    htmlImg.onload = () => {
      if (!fabricRef.current) return;
      if (asBackground) {
        // Novo fundo: reajusta o canvas à proporção dele e preenche por inteiro
        // (antes ficava a 90% com bordas cinzas e o export saía na escala errada)
        const oldBg = c.getObjects().find((o: any) => o.name === 'backgroundImage');
        isLoadingRef.current = true;
        if (oldBg) c.remove(oldBg);
        const { scale } = fitCanvasToNewSource(c, htmlImg.naturalWidth, htmlImg.naturalHeight);
        const fi = new fabric.Image(htmlImg, {
          left: 0, top: 0,
          scaleX: scale, scaleY: scale, selectable: false, evented: false, name: 'backgroundImage'
        });
        c.add(fi); c.sendToBack(fi);
        isLoadingRef.current = false;
      } else {
        const cw = c.getWidth(), ch = c.getHeight();
        const scale = Math.min(cw * 0.9 / htmlImg.naturalWidth, ch * 0.9 / htmlImg.naturalHeight, 1);
        const fi = new fabric.Image(htmlImg, {
          left: (cw - htmlImg.naturalWidth * scale) / 2, top: (ch - htmlImg.naturalHeight * scale) / 2,
          scaleX: scale, scaleY: scale, name: `AI: ${aiPrompt.substring(0, 20)}`
        });
        c.add(fi); c.setActiveObject(fi);
      }
      c.renderAll(); saveHistoryNow(); refreshLayers();
      toast.success(t('imageStudio.generated', 'Image generated!'));
    };
    let retried = false;
    htmlImg.onerror = () => {
      if (!retried) { retried = true; htmlImg.src = imageUrl; }
      else toast.error('Failed to load generated image');
    };
    htmlImg.src = `${API_URL}/image-studio/${imageId}/proxy?token=${token}`;
  };

  // Troca só a camada de fundo (ex.: após remover o fundo), preservando textos,
  // formas e demais camadas — antes o editor era remontado e edições não salvas
  // eram perdidas. Mantém posição/escala/rotação/flip do fundo antigo.
  const swapBackgroundImage = (newImage: GeneratedImage) => {
    const c = fabricRef.current;
    if (!c) return;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('token');
    const htmlImg = new window.Image();
    htmlImg.crossOrigin = 'anonymous';
    htmlImg.onload = () => {
      if (!fabricRef.current) return;
      const old: any = c.getObjects().find((o: any) => o.name === 'backgroundImage');
      const props = old
        ? { left: old.left, top: old.top, scaleX: old.scaleX, scaleY: old.scaleY, angle: old.angle, flipX: old.flipX, flipY: old.flipY, originX: old.originX, originY: old.originY }
        : { left: 0, top: 0 };
      isLoadingRef.current = true;
      if (old) c.remove(old);
      const fi = new fabric.Image(htmlImg, { ...props, selectable: false, evented: false, name: 'backgroundImage' });
      c.add(fi);
      c.sendToBack(fi);
      isLoadingRef.current = false;
      c.renderAll();
      saveHistoryNow();
      refreshLayers();
    };
    htmlImg.onerror = () => toast.error(t('imageStudio.loadError', 'Failed to load image'));
    htmlImg.src = `${API_URL}/image-studio/${newImage.id}/proxy?token=${token}`;
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim() || aiLoadingRef.current) return;
    const canvas = fabricRef.current;
    if (!canvas) return;

    aiLoadingRef.current = true;
    setAiLoading(true);
    try {
      // Get canvas snapshot to send to AI
      const imageBase64 = aiMode === 'edit' ? getCanvasBase64() : null;

      const response = await imageStudioService.aiEdit({
        prompt: aiPrompt.trim(),
        mode: aiMode,
        imageBase64: imageBase64 || undefined,
        style: aiStyle
      });

      if (!response.success || !response.data) {
        throw new Error('AI response failed');
      }

      const aiResult = response.data;
      const action = aiResult.action;

      // Show what the AI decided to do
      if (aiResult.description) {
        toast(aiResult.description, { icon: '🤖', duration: 4000 });
      }

      switch (action) {
        case 'generate_new': {
          if (aiResult.image?.original_url) {
            loadAIImageToCanvas(aiResult.image.original_url, aiResult.image.id, aiMode === 'edit');
          }
          break;
        }

        case 'remove_background': {
          // Trigger the background removal panel
          setShowBgRemover(true); setShowAI(false);
          toast('Use the Remove BG panel to process', { icon: '👉' });
          break;
        }

        case 'apply_filter': {
          const filterMap: Record<string, string> = {
            grayscale: 'bw', sepia: 'vintage', warm: 'warm', cool: 'cool', dramatic: 'dramatic', vintage: 'vintage'
          };
          const preset = FILTER_PRESETS.find(p => p.id === (filterMap[aiResult.filter] || aiResult.filter));
          if (preset) {
            setActiveFilter(preset.id); setFilterValues(preset.values); applyFilters(preset.values);
            setShowFilters(true); setShowAI(false);
            saveHistoryNow();
          }
          break;
        }

        case 'adjust': {
          const vals = {
            brightness: aiResult.brightness ?? 100,
            contrast: aiResult.contrast ?? 100,
            saturation: aiResult.saturation ?? 100,
            blur: aiResult.blur ?? 0,
            grayscale: aiResult.grayscale ?? 0,
            sepia: aiResult.sepia ?? 0
          };
          setFilterValues(vals); setActiveFilter('custom');
          applyFilters(vals);
          setShowFilters(true); setShowAI(false);
          saveHistoryNow();
          break;
        }

        case 'add_text': {
          const text = new fabric.IText(aiResult.text || 'Text', {
            left: canvas.getWidth() / 2 - 50,
            top: aiResult.position === 'top' ? 50 : aiResult.position === 'bottom' ? canvas.getHeight() - 80 : canvas.getHeight() / 2 - 20,
            fontFamily: 'Arial',
            fontSize: aiResult.fontSize || 36,
            fill: aiResult.color || '#ffffff',
            shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.5)', blur: 4, offsetX: 2, offsetY: 2 })
          });
          canvas.add(text); canvas.setActiveObject(text); canvas.renderAll();
          saveHistoryNow(); refreshLayers();
          setShowAI(false);
          break;
        }

        case 'error': {
          toast.error(aiResult.description || 'AI processing failed');
          break;
        }

        default: {
          toast.error('Unexpected AI response');
          break;
        }
      }

      setAiPrompt('');
    } catch (error: any) {
      const code = error.response?.data?.code || '';
      // PLAN_LIMIT_EXCEEDED: o interceptor global do api.ts já mostra o toast com CTA de upgrade
      if (code !== 'PLAN_LIMIT_EXCEEDED') {
        toast.error(error.response?.data?.message || t('imageStudio.generateError', 'Failed to generate'));
      }
    } finally {
      aiLoadingRef.current = false;
      setAiLoading(false);
    }
  };

  // Exporta com o viewport zerado: zoom/pan são só visuais e não podem afetar
  // o arquivo salvo (com zoom aplicado o export saía cortado ou com bordas).
  // O multiplier desfaz o fitScale para devolver a resolução nativa da origem.
  const exportCanvas = (format: 'png' | 'jpeg', quality?: number): { dataUrl: string; outW: number; outH: number } | null => {
    const canvas = fabricRef.current;
    if (!canvas) return null;
    if (cropRectRef.current) { cancelCrop(); setActiveTool('select'); }
    const active = canvas.getActiveObject();
    canvas.discardActiveObject();
    const prevVpt = (canvas.viewportTransform || [1, 0, 0, 1, 0, 0]).slice();
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    const m = Math.max(1, 1 / (fitScaleRef.current || 1));
    try {
      const dataUrl = canvas.toDataURL({ format, quality, multiplier: m });
      return { dataUrl, outW: Math.round(canvas.getWidth() * m), outH: Math.round(canvas.getHeight() * m) };
    } finally {
      canvas.setViewportTransform(prevVpt as any);
      // Regra do editor: a seleção do usuário não se perde por causa do export
      // (activeSelection não é restaurável após discard — só objetos simples)
      if (active && (active as any).type !== 'activeSelection') canvas.setActiveObject(active);
      canvas.renderAll();
    }
  };

  const handleDownload = async (format: 'png' | 'jpeg' = 'png') => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const ext = format === 'jpeg' ? 'jpg' : 'png';
    try {
      const out = exportCanvas(format, format === 'jpeg' ? 0.95 : undefined);
      if (out) {
        const link = document.createElement('a');
        link.download = `image-${Date.now()}.${ext}`;
        link.href = out.dataUrl;
        link.click();
        return;
      }
    } catch (err: any) {
      console.warn('Canvas toDataURL failed (canvas may be tainted), falling back to proxy download', err);
    }

    if (!image.id) {
      toast.error('Falha ao baixar imagem');
      return;
    }
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const token = localStorage.getItem('token');
    const filename = `image-${Date.now()}`;
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    params.set('download', '1');
    params.set('filename', filename);
    const proxyUrl = `${API_URL}/image-studio/${image.id}/proxy?${params.toString()}`;
    const link = document.createElement('a');
    link.href = proxyUrl;
    link.download = `${filename}.${ext}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Gira o canvas inteiro (fundo + camadas) em 90°, trocando largura/altura.
  // O rotate() antigo girava só o fundo em torno do canto superior esquerdo,
  // jogando a imagem pra fora da área visível.
  const rotateCanvas = (dir: 1 | -1) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    if (cropRectRef.current) { cancelCrop(); setActiveTool('select'); }
    const w0 = canvas.getWidth(), h0 = canvas.getHeight();
    isLoadingRef.current = true;
    canvas.setDimensions({ width: h0, height: w0 });
    baseDimsRef.current = { w: h0, h: w0 };
    canvas.getObjects().forEach((o: any) => {
      const cp = o.getCenterPoint();
      const nx = dir === 1 ? h0 - cp.y : cp.y;
      const ny = dir === 1 ? cp.x : w0 - cp.x;
      o.set('angle', ((o.angle || 0) + dir * 90) % 360);
      o.setPositionByOrigin(new fabric.Point(nx, ny), 'center', 'center');
      o.setCoords();
    });
    isLoadingRef.current = false;
    canvas.renderAll();
    saveHistoryNow();
  };

  // ===== Canvas actions =====
  const doAction = (action: string) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const bg = canvas.getObjects().find((o: any) => o.name === 'backgroundImage');
    switch (action) {
      case 'rotate-left': rotateCanvas(-1); break;
      case 'rotate-right': rotateCanvas(1); break;
      case 'flip-h': if (bg) { bg.set('flipX', !bg.flipX); canvas.renderAll(); saveHistoryNow(); } break;
      case 'flip-v': if (bg) { bg.set('flipY', !bg.flipY); canvas.renderAll(); saveHistoryNow(); } break;
      case 'undo': if (historyIndexRef.current > 0) { historyIndexRef.current--; loadFromHistory(); } break;
      case 'redo': if (historyIndexRef.current < historyRef.current.length - 1) { historyIndexRef.current++; loadFromHistory(); } break;
      case 'zoom-in': setZoom(z => { const nz = Math.min(z + 0.15, 4); canvas.setZoom(nz); canvas.renderAll(); return nz; }); break;
      case 'zoom-out': setZoom(z => { const nz = Math.max(z - 0.15, 0.2); canvas.setZoom(nz); canvas.renderAll(); return nz; }); break;
      case 'zoom-reset': setZoom(1); canvas.setZoom(1); canvas.renderAll(); break;
    }
  };

  const handleExport = (format: 'png' | 'jpeg' = 'png', quality: number = 1) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    try {
      const mime = format === 'jpeg' ? 'image/jpeg' : 'image/png';
      const out = exportCanvas(format, format === 'jpeg' ? quality : undefined);
      if (!out) return;
      onSave(out.dataUrl, out.outW, out.outH, mime);
    } catch (err: any) {
      console.error('Export failed:', err);
      toast.error(err?.message?.includes('tainted') ? 'Cannot export: image has CORS restrictions. Try saving from the gallery instead.' : 'Export failed');
    }
  };

  // Filters
  const handleFilterChange = (preset: FilterPreset) => { setActiveFilter(preset.id); setFilterValues(preset.values); applyFilters(preset.values); saveHistoryNow(); };
  const handleCustomFilter = (key: string, value: number) => { const nv = { ...filterValues, [key]: value }; setFilterValues(nv); setActiveFilter('custom'); applyFilters(nv); saveHistory(); };

  const applyFilters = (vals: typeof filterValues) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const bg = canvas.getObjects().find((o: any) => o.name === 'backgroundImage') as any;
    if (!bg) return;
    bg.filters = [];
    if (vals.brightness !== 100) bg.filters.push(new fabric.Image.filters.Brightness({ brightness: (vals.brightness - 100) / 100 }));
    if (vals.contrast !== 100) bg.filters.push(new fabric.Image.filters.Contrast({ contrast: (vals.contrast - 100) / 100 }));
    if (vals.saturation !== 100) bg.filters.push(new fabric.Image.filters.Saturation({ saturation: (vals.saturation - 100) / 100 }));
    if (vals.blur > 0) bg.filters.push(new fabric.Image.filters.Blur({ blur: vals.blur / 100 }));
    if (vals.grayscale > 0) bg.filters.push(new fabric.Image.filters.Grayscale());
    if (vals.sepia > 0) bg.filters.push(new fabric.Image.filters.Sepia());
    bg.applyFilters(); canvas.renderAll();
  };

  const isCropRect = selectedObj && selectedObj.name === 'cropRect';
  const isTextObj = !isCropRect && selectedObj && (selectedObj.type === 'i-text' || selectedObj.type === 'textbox' || selectedObj.type === 'text');
  const isShapeObj = !isCropRect && selectedObj && selectedObj.name !== 'backgroundImage';

  const tools: { id: Tool; icon: any; label: string }[] = [
    { id: 'select', icon: MousePointer2, label: t('imageStudio.editor.select', 'Select') },
    { id: 'crop', icon: Crop, label: t('imageStudio.editor.crop', 'Crop') },
    { id: 'text', icon: Type, label: t('imageStudio.editor.text', 'Text') },
    { id: 'draw', icon: Pencil, label: t('imageStudio.editor.draw', 'Draw') },
    { id: 'shape', icon: Square, label: t('imageStudio.editor.shapes', 'Shapes') },
  ];

  // Hidden file input for adding image layers
  const renderImageInput = (
    <input ref={imageInputRef} type="file" accept="image/*" onChange={addImageLayer} className="hidden" />
  );

  return (
    <div className="flex h-full bg-gray-100 dark:bg-gray-900">
      {/* Left Toolbar */}
      <div className="flex flex-col gap-1 p-2 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 w-14 shrink-0">
        {tools.map(({ id, icon: Icon, label }) => (
          <button key={id} onClick={() => handleToolChange(id)} title={label}
            className={`p-2.5 rounded-lg transition-colors ${activeTool === id ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
            <Icon className="w-5 h-5" />
          </button>
        ))}

        {/* Add Image Layer */}
        <button onClick={() => imageInputRef.current?.click()} title="Add Image Layer"
          className="p-2.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <ImagePlus className="w-5 h-5" />
        </button>

        {/* Layers panel toggle */}
        <button onClick={() => setShowLayers(!showLayers)} title="Layers"
          className={`p-2.5 rounded-lg transition-colors ${showLayers ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
          <Layers className="w-5 h-5" />
        </button>

        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

        {[
          { a: 'rotate-left', i: RotateCcw, l: 'Rotate Left' },
          { a: 'rotate-right', i: RotateCw, l: 'Rotate Right' },
          { a: 'flip-h', i: FlipHorizontal, l: 'Flip H' },
          { a: 'flip-v', i: FlipVertical, l: 'Flip V' },
        ].map(({ a, i: Icon, l }) => (
          <button key={a} onClick={() => doAction(a)} title={l}
            className="p-2.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <Icon className="w-5 h-5" />
          </button>
        ))}

        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

        <button onClick={() => doAction('undo')} disabled={!canUndo} title="Undo (Ctrl+Z)"
          className="p-2.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 transition-colors">
          <Undo2 className="w-5 h-5" />
        </button>
        <button onClick={() => doAction('redo')} disabled={!canRedo} title="Redo (Ctrl+Y)"
          className="p-2.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 transition-colors">
          <Redo2 className="w-5 h-5" />
        </button>

        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

        <button onClick={() => doAction('zoom-in')} title="Zoom In" className="p-2.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <ZoomIn className="w-5 h-5" />
        </button>
        <button onClick={() => doAction('zoom-reset')} title="Reset Zoom"
          className="text-center text-[10px] text-gray-500 hover:text-gray-700 cursor-pointer py-1">{Math.round(zoom * 100)}%</button>
        <button onClick={() => doAction('zoom-out')} title="Zoom Out" className="p-2.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <ZoomOut className="w-5 h-5" />
        </button>
      </div>

      {/* Center: options bar + canvas */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Options Bar */}
        <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 min-h-[48px] overflow-x-auto">
          {/* Crop tool: ratio presets + apply/cancel */}
          {activeTool === 'crop' && (
            <div className="flex items-center gap-2 shrink-0">
              {([
                { l: '1:1', r: 1 },
                { l: '16:9', r: 16 / 9 },
                { l: '9:16', r: 9 / 16 },
                { l: '4:5', r: 4 / 5 },
              ] as const).map(({ l, r }) => (
                <button key={l} onClick={() => setCropRatio(r)}
                  className="px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                  {l}
                </button>
              ))}
              <button onClick={applyCrop}
                className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700">
                {t('imageStudio.editor.applyCrop', 'Aplicar corte')}
              </button>
              <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />
              <label className="text-xs text-gray-500 dark:text-gray-400">{t('imageStudio.editor.area', 'Área (px)')}</label>
              <input type="number" min={10} max={5000} value={areaW || ''}
                onChange={(e) => setAreaW(parseInt(e.target.value) || 0)}
                className="w-16 px-1 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-center text-gray-900 dark:text-white" />
              <span className="text-xs text-gray-400">×</span>
              <input type="number" min={10} max={5000} value={areaH || ''}
                onChange={(e) => setAreaH(parseInt(e.target.value) || 0)}
                className="w-16 px-1 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-center text-gray-900 dark:text-white" />
              <button onClick={applyAreaResize}
                className="px-2 py-1 rounded-lg border border-primary-500 text-primary-600 dark:text-primary-400 text-xs font-medium hover:bg-primary-50 dark:hover:bg-primary-900/20">
                {t('imageStudio.editor.applyArea', 'Aplicar área')}
              </button>
              <button onClick={() => handleToolChange('select')}
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                {t('common.cancel', 'Cancelar')}
              </button>
            </div>
          )}

          {/* Text tool: click-to-place hint + fallback button */}
          {activeTool === 'text' && !selectedObj && (
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {t('imageStudio.editor.clickToAddText', 'Clique na imagem onde quer o texto')}
              </span>
              <button onClick={() => addText()} className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 shrink-0">
                + {t('imageStudio.editor.addText', 'Add Text')}
              </button>
            </div>
          )}

          {/* Shape tool: shape buttons */}
          {activeTool === 'shape' && (
            <div className="flex gap-1 shrink-0">
              {SHAPE_TYPES.map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => addShape(id)} title={label}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          )}

          {/* Draw tool: brush options */}
          {activeTool === 'draw' && (
            <div className="flex items-center gap-3 shrink-0">
              <label className="text-xs text-gray-500">Color</label>
              <input type="color" value={brushColor} onChange={(e) => { setBrushColor(e.target.value); if (fabricRef.current) fabricRef.current.freeDrawingBrush.color = e.target.value; }}
                className="w-8 h-8 rounded border border-gray-300 cursor-pointer" />
              <label className="text-xs text-gray-500">Size</label>
              <input type="range" min={1} max={50} value={brushSize}
                onChange={(e) => { const s = parseInt(e.target.value); setBrushSize(s); if (fabricRef.current) fabricRef.current.freeDrawingBrush.width = s; }}
                className="w-28 accent-primary-600" />
              <span className="text-xs text-gray-500 w-8">{brushSize}px</span>
            </div>
          )}

          {/* Selected text object: font controls */}
          {isTextObj && (
            <div className="flex items-center gap-2 shrink-0">
              <select value={objFontFamily} onChange={(e) => setFont(e.target.value)}
                className="px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white w-36">
                {FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
              </select>
              <input type="number" min={8} max={200} value={objFontSize} onChange={(e) => setFSize(parseInt(e.target.value) || 32)}
                className="w-16 px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white text-center" />
              <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                <button onClick={toggleBold} title="Bold" className={`p-1.5 ${objBold ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                  <Bold className="w-4 h-4" />
                </button>
                <button onClick={toggleItalic} title="Italic" className={`p-1.5 ${objItalic ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                  <Italic className="w-4 h-4" />
                </button>
                <button onClick={toggleUnderline} title="Underline" className={`p-1.5 ${objUnderline ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                  <Underline className="w-4 h-4" />
                </button>
              </div>
              <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                {(['left', 'center', 'right'] as const).map(a => {
                  const Icon = a === 'left' ? AlignLeft : a === 'center' ? AlignCenter : AlignRight;
                  return (
                    <button key={a} onClick={() => setAlign(a)} title={`Align ${a}`}
                      className={`p-1.5 ${objTextAlign === a ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                      <Icon className="w-4 h-4" />
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500">Color</label>
                <input type="color" value={objFill} onChange={(e) => setFill(e.target.value)} className="w-7 h-7 rounded border border-gray-300 cursor-pointer" />
              </div>
            </div>
          )}

          {/* Selected shape object: fill / stroke controls */}
          {isShapeObj && !isTextObj && (
            <div className="flex items-center gap-3 shrink-0">
              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500">Fill</label>
                <input type="color" value={objFill === 'transparent' ? '#ffffff' : objFill} onChange={(e) => setFill(e.target.value)} className="w-7 h-7 rounded border border-gray-300 cursor-pointer" />
                <button onClick={() => setFill('transparent')} title="No fill" className="text-[10px] text-gray-400 hover:text-gray-600 px-1">∅</button>
              </div>
              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500">Stroke</label>
                <input type="color" value={objStroke} onChange={(e) => setStroke(e.target.value)} className="w-7 h-7 rounded border border-gray-300 cursor-pointer" />
              </div>
              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500">Width</label>
                <input type="number" min={0} max={20} value={objStrokeWidth} onChange={(e) => setStrokeW(parseInt(e.target.value) || 0)}
                  className="w-14 px-1 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-center" />
              </div>
            </div>
          )}

          {/* Any selected: opacity, layer, lock, delete, duplicate */}
          {isShapeObj && (
            <div className="flex items-center gap-2 shrink-0 ml-2 pl-2 border-l border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500">Opacity</label>
                <input type="range" min={0} max={100} value={objOpacity} onChange={(e) => setOpacity(parseInt(e.target.value))} className="w-20 accent-primary-600" />
                <span className="text-xs text-gray-500 w-8">{objOpacity}%</span>
              </div>
              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500" title="Rotation">°</label>
                <input type="number" min={-360} max={360} step={15} value={Math.round(selectedObj?.angle || 0)}
                  onChange={(e) => updateProp('angle', parseInt(e.target.value) || 0)}
                  className="w-14 px-1 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-center" />
              </div>
              <button onClick={bringForward} title="Bring Forward" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"><MoveUp className="w-4 h-4" /></button>
              <button onClick={sendBackward} title="Send Backward" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"><MoveDown className="w-4 h-4" /></button>
              <button onClick={toggleLock} title={objLocked ? 'Unlock' : 'Lock'} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
                {objLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
              </button>
              <button onClick={duplicateSelected} title="Duplicate" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"><Copy className="w-4 h-4" /></button>
              <button onClick={deleteSelected} title="Delete" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="w-4 h-4" /></button>
            </div>
          )}

          {/* Right: AI, filters, bg, download, save */}
          <div className="ml-auto flex items-center gap-1.5 shrink-0">
            <button onClick={() => { setShowAI(!showAI); setShowFilters(false); setShowBgRemover(false); }}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm ${showAI ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
              <Sparkles className="w-4 h-4" /> AI
            </button>
            <button onClick={() => { setShowFilters(!showFilters); setShowBgRemover(false); setShowAI(false); }}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm ${showFilters ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
              <Sliders className="w-4 h-4" /> Filters
            </button>
            <button onClick={() => { setShowBgRemover(!showBgRemover); setShowFilters(false); setShowAI(false); }}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm ${showBgRemover ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
              <Eraser className="w-4 h-4" /> BG
            </button>
            <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />
            <button onClick={() => handleDownload('png')} title="Download PNG" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
              <Download className="w-4 h-4" />
            </button>
            <button onClick={() => handleExport('png')} className="px-2.5 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700">
              <span className="flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> Save</span>
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-auto flex items-center justify-center p-4" style={{ background: 'repeating-conic-gradient(#d1d5db 0% 25%, transparent 0% 50%) 50% / 20px 20px' }}>
          <div className="shadow-2xl"><canvas ref={canvasRef} /></div>
        </div>
      </div>

      {/* Right Panels */}
      {showFilters && (
        <div className="w-64 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto shrink-0">
          <ImageFiltersPanel activeFilter={activeFilter} onFilterChange={handleFilterChange} onCustomChange={handleCustomFilter} customValues={filterValues} />
        </div>
      )}
      {showBgRemover && (
        <div className="w-72 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto shrink-0">
          <BackgroundRemover image={image} onComplete={(newImage) => { setShowBgRemover(false); swapBackgroundImage(newImage); onBgRemoved?.(newImage); }} />
        </div>
      )}

      {/* AI Panel */}
      {showAI && (
        <div className="w-72 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto shrink-0">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-purple-500" /> AI Assistant
            </h3>
          </div>
          <div className="p-3 space-y-3">
            {/* Mode */}
            <div>
              <label className="text-[11px] text-gray-500 mb-1 block">Mode</label>
              <div className="flex gap-1">
                <button onClick={() => setAiMode('generate')}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${aiMode === 'generate' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                  Generate New
                </button>
                <button onClick={() => setAiMode('edit')}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${aiMode === 'edit' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                  Edit / Replace BG
                </button>
              </div>
            </div>

            {/* Style */}
            <div>
              <label className="text-[11px] text-gray-500 mb-1 block">Style</label>
              <select value={aiStyle} onChange={(e) => setAiStyle(e.target.value)}
                className="w-full px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-gray-900 dark:text-white">
                <option value="realistic">Realistic</option>
                <option value="illustration">Illustration</option>
                <option value="minimalist">Minimalist</option>
                <option value="3d">3D Render</option>
                <option value="corporate">Corporate</option>
                <option value="creative">Creative</option>
              </select>
            </div>

            {/* Prompt */}
            <div>
              <label className="text-[11px] text-gray-500 mb-1 block">
                {aiMode === 'edit' ? 'Describe the changes' : 'Describe the image'}
              </label>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder={aiMode === 'edit'
                  ? 'Ex: Remove the background and keep only the text, Apply a vintage filter, Make it black and white...'
                  : 'Ex: Using this as reference, create a football team playing on this field...'}
                rows={4}
                className="w-full px-2.5 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white placeholder-gray-400 resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={aiLoading}
              />
            </div>

            {/* Info */}
            <p className="text-[10px] text-gray-400">
              {aiMode === 'edit'
                ? 'The AI analyzes your current image and decides the best action: remove background, apply filters, adjust colors, add text, or generate a replacement.'
                : 'Uses your image as reference to generate new content with AI.'}
            </p>

            <AiCostBadge feature="image-studio" action="ai-edit" />
            {/* Generate button */}
            <button onClick={handleAIGenerate} disabled={aiLoading || !aiPrompt.trim()}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-medium hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 transition-all">
              {aiLoading ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</>
              ) : (
                <><Wand2 className="w-3.5 h-3.5" /> {aiMode === 'edit' ? 'Apply with AI' : 'Generate Image'}</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Layers Panel */}
      {showLayers && (
        <div className="w-64 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto shrink-0">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Layers</h3>
            <button onClick={() => imageInputRef.current?.click()} title="Add Image Layer"
              className="p-1 rounded text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20">
              <ImagePlus className="w-4 h-4" />
            </button>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {layersList.map((layer, i) => {
              const isBg = layer.name === 'backgroundImage';
              const isActive = fabricRef.current?.getActiveObject() === layer.obj;
              return (
                <div key={i}
                  onClick={() => selectLayer(layer.obj)}
                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs transition-colors ${
                    isActive ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}>
                  {/* Visibility toggle */}
                  <button onClick={(e) => { e.stopPropagation(); toggleLayerVisibility(layer.obj); }}
                    className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0">
                    {layer.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 text-gray-300" />}
                  </button>

                  {/* Layer preview (tiny thumbnail) */}
                  <div className="w-8 h-8 rounded border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-900 overflow-hidden shrink-0 flex items-center justify-center">
                    {layer.type === 'image' ? (
                      <img src={layer.obj._element?.src || ''} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[8px] text-gray-400">{layer.type}</span>
                    )}
                  </div>

                  {/* Name */}
                  <span className={`truncate flex-1 ${isBg ? 'text-gray-400 italic' : 'text-gray-700 dark:text-gray-300'}`}>
                    {isBg ? 'Background' : layer.name}
                  </span>

                  {/* Lock indicator */}
                  {layer.locked && <Lock className="w-3 h-3 text-gray-400 shrink-0" />}
                </div>
              );
            })}
            {layersList.length === 0 && (
              <p className="p-3 text-xs text-gray-400 text-center">No layers</p>
            )}
          </div>
        </div>
      )}

      {/* Hidden file input for image layers */}
      {renderImageInput}
    </div>
  );
};

export default ImageEditor;
